/**
 * PageViewTracker — fires a beacon to /api/track/pageview/ on every route change.
 * Generates a persistent session_id (stored in localStorage) and attaches the
 * current Supabase user_id if the visitor is signed in.
 */
import { useEffect, useRef } from 'react';
import { useLocation } from 'react-router-dom';
import { supabase } from '../supabaseClient';

const API_BASE =
  process.env.NODE_ENV === 'production'
    ? 'https://yemberzal-app-28481d1d0b39.herokuapp.com/api'
    : 'http://127.0.0.1:8000/api';

function getSessionId() {
  let sid = localStorage.getItem('yz_session_id');
  if (!sid) {
    sid = crypto.randomUUID?.() || `${Date.now()}-${Math.random().toString(36).slice(2)}`;
    localStorage.setItem('yz_session_id', sid);
  }
  return sid;
}

export default function PageViewTracker() {
  const location = useLocation();
  const prevPath = useRef(null);

  useEffect(() => {
    // Avoid double-firing on same path
    if (location.pathname === prevPath.current) return;
    prevPath.current = location.pathname;

    const track = async () => {
      try {
        let userId = '';
        try {
          const { data: { user } } = await supabase.auth.getUser();
          userId = user?.id || '';
        } catch {
          // not signed in
        }

        const payload = {
          page_path: location.pathname,
          referrer: document.referrer || '',
          session_id: getSessionId(),
          user_id: userId,
        };

        // Use sendBeacon with Blob for reliability; fall back to fetch
        const url = `${API_BASE}/track/pageview/`;
        const body = JSON.stringify(payload);
        if (navigator.sendBeacon) {
          const blob = new Blob([body], { type: 'application/json' });
          navigator.sendBeacon(url, blob);
        } else {
          fetch(url, {
            method: 'POST',
            headers: { 'Content-Type': 'application/json' },
            body,
            keepalive: true,
          }).catch(() => {});
        }
      } catch {
        // tracking failures are silent
      }
    };

    track();
  }, [location.pathname]);

  return null; // renders nothing
}
